/**
 * render.js — все render-функции
 * Проект: TorgiRT (shared — все витрины)
 */

import { state, refs, filterCodeToName, BASE_PATH } from './state';
import {
  escapeHtml, formatPrice, formatMoney, SF_CURRENCY_HTML, getCartTotal, getCartUnitsCount, getCartQuantity,
  lockBodyScroll, unlockBodyScroll, isCartAllowedPage, isOnZakazPage,
  updateDocumentTitle, toWebpSrc, setPictureImageSrc,
} from './helpers';
import { ICONS } from './icons';
import { isFavorite, getFavoritesCount } from './favorites';
import { countFallbackResults } from './catalog';
import { updateFloatingCartText, updateFloatingCartVisibility } from './floating-cart';
import { shiftModalImage } from './modal';
import { initSwipe } from './swipe';
import { mountPdpCrossSell, removePdpCrossSell } from './pdp-cross-sell';
import { badgeMarketingLabel, orderedGridBadges, renderGridBadgeStack } from './product-badges';
import type { CartItem, Product } from './types';

/* ── Products count pill: track previous value for animation ── */
let _prevFilteredCount: number | null = null;

const CATALOG_ROOT_LABEL = "Каталог";

/* ── Кнопка избранного (♡ / ♥) ── */

/** Генерирует HTML кнопки-сердечка */
function renderFavBtn(article: string, extraClass?: string) {
  const active = isFavorite(article);
  const cls = 'fav-btn' + (active ? ' is-fav' : '') + (extraClass ? ' ' + extraClass : '');
  return '<button type="button"' +
    ' class="' + cls + '"' +
    ' data-action="toggle-favorite"' +
    ' data-article="' + escapeHtml(article) + '"' +
    ' aria-label="' + (active ? 'Убрать из избранного' : 'Добавить в избранное') + '"' +
    ' aria-pressed="' + active + '"' +
    '>' + (active ? ICONS.heartFilled : ICONS.heart) + '</button>';
}

/* ── Product marketing badges (H.2) ── */

const BADGE_CSS_CLASS: Record<string, string> = {
  'new': 'product-badge--new',
  'hit': 'product-badge--hit',
  'sale': 'product-badge--sale',
};

/** Каталог list: бейджи под названием (stock + маркетинг, порядок как плитка v3.1) */
function renderCatalogListBadges(
  badges: string[] | undefined,
  oldPrice: number | null,
  price: number,
  stock: number,
): string {
  const items = [`<span class="product-badge product-badge--stock list-name-stock">Шт. ${stock}</span>`];
  for (const b of orderedGridBadges(badges)) {
    const label = badgeMarketingLabel(b, oldPrice, price);
    const cls = BADGE_CSS_CLASS[b] ?? '';
    items.push(`<span class="product-badge ${cls}">${escapeHtml(label)}</span>`);
  }
  return `<div class="product-badges list-name-badges">${items.join('')}</div>`;
}

function resolveCartDisplayMeta(item: CartItem): {
  badges: string[];
  oldPrice: number | null;
  price: number;
  stock: number;
} {
  const product = state.products.find((p) => p.article === item.article);
  const badges = item.badges ?? product?.badges ?? [];
  const oldPrice = item.oldPrice ?? product?.oldPrice ?? null;
  const stock = item.stock ?? product?.stock ?? 0;
  return { badges, oldPrice, price: item.price, stock };
}

function pictureTag(
  src: string,
  alt: string,
  loading: string = 'lazy',
  imgAttrs: Record<string, string> = {},
): string {
  const attrStr = Object.entries(imgAttrs)
    .map(([key, value]) => ` ${key}="${escapeHtml(value)}"`)
    .join('');
  const webp = toWebpSrc(src);
  if (!webp) {
    return `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" loading="${loading}" decoding="async"${attrStr} />`;
  }
  return `<picture><source srcset="${escapeHtml(webp)}" type="image/webp" /><img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" loading="${loading}" decoding="async"${attrStr} /></picture>`;
}

/* ── Render functions ── */

export function renderCartBadge() {
  if (!refs.cartBadge) return;
  const count = getCartUnitsCount();
  refs.cartBadge.textContent = String(count);
  refs.cartBadge.classList.toggle("hidden", count === 0);
  const cartLink = refs.cartBadge.closest('.cart-btn-wrap')?.querySelector('.cart-button');
  if (cartLink) {
    cartLink.setAttribute('aria-label',
      count === 0 ? 'Корзина' : `Корзина: ${pluralizeProducts(count)}`
    );
  }
  const bottomCartBadge = document.getElementById('bottom-nav-cart-badge');
  if (bottomCartBadge) {
    bottomCartBadge.textContent = String(count);
    bottomCartBadge.classList.toggle('hidden', count === 0);
  }
}

export function renderBottomNavFavBadge(): void {
  const badge = document.getElementById('bottom-nav-fav-badge');
  if (!badge) return;
  const count = getFavoritesCount();
  badge.textContent = String(count);
  badge.classList.toggle('hidden', count === 0);
}

let announceTimer: ReturnType<typeof setTimeout> | null = null;
export function announceCartChange(message: string) {
  const el = document.getElementById('cart-announcer');
  if (!el) return;
  el.textContent = '';
  if (announceTimer !== null) clearTimeout(announceTimer);
  announceTimer = setTimeout(() => { el.textContent = message; }, 100);
}

export function renderCartSummary() {
  if (!refs.cartSummary) return;
  if (state.cart.length === 0 || !isCartAllowedPage()) {
    // На странице заказа при пустой корзине — возврат в каталог
    // НО: не редиректим, если это страница успешного оформления заказа (order-success)
    const isOrderSuccess = !!document.querySelector('[data-order-success]');
    if (isOnZakazPage() && state.cart.length === 0 && !isOrderSuccess) {
      window.location.href = BASE_PATH;
      return;
    }
    refs.cartSummary.classList.add("hidden");
    refs.cartSummary.innerHTML = "";
    return;
  }

  const totalText = `Корзина: итого ${formatMoney(getCartTotal())}`;

  refs.cartSummary.classList.remove("hidden");

  // На странице заказа — только «Вернуться к выбору»
  if (isOnZakazPage()) {
    refs.cartSummary.innerHTML = `
      <div class="container">
        <span>${totalText}</span>
        <a href="${BASE_PATH}" class="btn btn-summary btn-sm">Вернуться к выбору</a>
      </div>
    `;
    return;
  }

  const toggleLabel = state.cartOpen ? "Скрыть корзину" : "Показать корзину";
  if (!state.cartOpen) {
    refs.cartSummary.innerHTML = `
      <div class="container">
        <span>${totalText}</span>
        <button type="button" class="btn btn-summary btn-sm" data-action="toggle-cart-summary">${toggleLabel}</button>
      </div>
    `;
    return;
  }

  refs.cartSummary.innerHTML = `
    <div class="container">
      <span>${totalText}</span>
      <div class="cart-summary-actions">
        <button type="button" class="btn btn-summary btn-sm btn-summary-strong" data-action="go-to-zakaz">Оформить заказ</button>
        <button type="button" class="btn btn-summary btn-sm" data-action="toggle-cart-summary">${toggleLabel}</button>
      </div>
    </div>
  `;
}

export function renderCartItemsGrid() {
  return `
    <div class="cart-grid">
      ${state.cart
        .map((item) => {
          const meta = resolveCartDisplayMeta(item);
          const firstImage = item.images?.[0] ?? "";
          const stock = meta.stock;
          const atLimit = stock > 0 && item.quantity >= stock;
          return `
            <article class="product-card" data-article="${escapeHtml(item.article)}">
              <div class="product-image" data-action="open-product" data-article="${escapeHtml(item.article)}" tabindex="0" role="button">
                ${
                  firstImage
                    ? pictureTag(firstImage, item.name)
                    : `<div class="placeholder">${ICONS.packageLg}</div>`
                }
                ${renderGridBadgeStack(meta.badges, meta.oldPrice, meta.price, stock)}
                ${renderFavBtn(item.article, 'fav-btn-card')}
              </div>
              <div class="product-content">
                <h3 class="product-title">
                  <span class="product-title-text" data-action="open-product" data-article="${escapeHtml(item.article)}" tabindex="0" role="button">${escapeHtml(item.name)}</span>
                  <a href="${BASE_PATH}/product/${encodeURIComponent(item.article)}"
                     class="product-page-link"
                     aria-label="Страница товара — ${escapeHtml(item.name)}"
                     title="Страница товара">${ICONS.moreVertical}</a>
                </h3>
                <div class="price-line"><span class="price-value">${formatPrice(item.price)}</span><span class="price-unit">\u00A0₽</span>${meta.oldPrice && meta.oldPrice > item.price ? `<span class="old-price">${formatMoney(meta.oldPrice)}</span>` : ''}</div>
                ${renderGridCardActions(item.article, item.quantity, item.price, stock, atLimit, false)}
              </div>
            </article>
          `;
        })
        .join("")}
    </div>
  `;
}

export function renderCartItemsList() {
  return `
    <section class="cart-list card">
      <div class="list-header">
        <div class="list-header-product">Товар</div>
        <div class="center">Цена</div>
        <div class="center">Шт.</div>
        <div class="center">Кол-во</div>
        <div class="center">Сумма</div>
      </div>
      <div class="list-body">
        ${state.cart
          .map((item) => {
            const meta = resolveCartDisplayMeta(item);
            const firstImage = item.images?.[0] ?? "";
            const itemTotal = item.quantity * item.price;
            const stock = meta.stock;
            const atLimit = stock > 0 && item.quantity >= stock;
            return `
              <div class="list-row" data-article="${escapeHtml(item.article)}">
                <div class="thumb-48 list-col-thumb" data-action="open-product" data-article="${escapeHtml(item.article)}" tabindex="0" role="button">
                  ${
                    firstImage
                      ? pictureTag(firstImage, item.name)
                      : `<div class="placeholder">${ICONS.packageSm}</div>`
                  }
                  ${renderFavBtn(item.article, 'fav-btn-list')}
                </div>
                <div class="list-col-name">
                  <h3 class="list-product-name">
                    <span class="product-title-text" data-action="open-product" data-article="${escapeHtml(item.article)}" tabindex="0" role="button">${escapeHtml(item.name)}</span>
                    <a href="${BASE_PATH}/product/${encodeURIComponent(item.article)}"
                       class="product-page-link"
                       aria-label="Страница товара — ${escapeHtml(item.name)}"
                       title="Страница товара">${ICONS.moreVertical}</a>
                  </h3>
                  ${renderCatalogListBadges(meta.badges, meta.oldPrice, meta.price, stock)}
                </div>
                <div class="center list-col-price">
                  <div class="list-price-stack">
                    <div class="list-price-main">
                      <span class="list-price-amount">${formatPrice(item.price)}</span><span class="list-price-currency">\u00A0₽</span>
                    </div>
                    ${meta.oldPrice && meta.oldPrice > item.price ? `<span class="old-price">${formatMoney(meta.oldPrice)}</span>` : ''}
                  </div>
                </div>
                <div class="center list-col-stock"><span class="list-unit-badge">${stock}</span></div>
                <div class="center list-col-action">
                  ${renderListRowActions(item.article, item.quantity, stock, atLimit, false)}
                </div>
                <div class="center list-col-total${item.quantity > 0 ? "" : " is-empty"}">
                  ${renderListRowTotal(itemTotal, item.quantity)}
                </div>
              </div>
            `;
          })
          .join("")}
      </div>
    </section>
  `;
}

export function renderCartItems() {
  if (!refs.cartItems) return;
  const onZakaz = isOnZakazPage();
  if (state.cart.length === 0 || !state.cartOpen || !isCartAllowedPage()) {
    if (!onZakaz && refs.cartItemsWrap) refs.cartItemsWrap.classList.add("hidden");
    refs.cartItems.innerHTML = "";
    return;
  }

  if (!onZakaz && refs.cartItemsWrap) refs.cartItemsWrap.classList.remove("hidden");
  refs.cartItems.innerHTML = onZakaz
    ? renderCartItemsList()
    : state.viewMode === "grid"
      ? renderCartItemsGrid()
      : renderCartItemsList();
}

const FLYOUT_MULTI_THRESHOLD = 8;

export function renderCatalog() {
  if (!refs.catalogDropdown) return;

  const triggerBtn = refs.catalogWrap?.querySelector('[data-action="toggle-catalog"]');
  if (triggerBtn) triggerBtn.setAttribute("aria-expanded", String(!!state.catalogOpen));

  refs.catalogDropdown.setAttribute("role", "menu");
  refs.catalogDropdown.setAttribute("aria-label", "Каталог товаров");

  const categories = state.catalogData.categories;

  if (state.catalogOpen && state.expandedCategories.size === 0 && categories.length > 0) {
    const seedId =
      state.selectedCategory != null &&
      categories.some((c) => c.id === state.selectedCategory)
        ? state.selectedCategory
        : categories[0]?.id;
    if (seedId) state.expandedCategories.add(seedId);
  }

  const activeId =
    [...state.expandedCategories].find((id) => categories.some((c) => c.id === id)) ??
    categories[0]?.id ??
    "";
  const activeCategory = categories.find((c) => c.id === activeId);
  const activeSubs = activeCategory?.subcategories ?? [];

  refs.catalogDropdown.classList.toggle("catalog-dropdown--flyout", state.catalogOpen);
  refs.catalogDropdown.classList.toggle(
    "catalog-dropdown--multi",
    state.catalogOpen && activeSubs.length > FLYOUT_MULTI_THRESHOLD,
  );

  const railHtml = categories
    .map((category) => {
      const isActive = category.id === activeId;
      const totalInCategory = category.subcategories.reduce((sum, subcategory) => {
        return sum + (subcategory.productCount ?? 0);
      }, 0);

      return `
        <button
          type="button"
          class="catalog-main${isActive ? " is-active" : ""}"
          role="menuitem"
          aria-expanded="${isActive}"
          data-action="select-category"
          data-category-id="${escapeHtml(category.id ?? "")}"
          data-hover-category="${escapeHtml(category.id ?? "")}"
        >
          <span class="catalog-main-name">${escapeHtml(category.name)}</span>
          <span class="pill">${totalInCategory} товаров</span>
        </button>
      `;
    })
    .join("");

  const flyoutHtml = activeSubs
    .map((subcategory) => {
      return `
        <button
          type="button"
          class="catalog-sub"
          role="menuitem"
          data-action="select-subcategory"
          data-category-id="${escapeHtml(activeCategory?.id ?? "")}"
          data-subcategory-id="${escapeHtml(subcategory.id ?? "")}"
        >
          <span>${escapeHtml(subcategory.name)}</span>
          <span class="pill">${subcategory.productCount ?? 0}</span>
        </button>
      `;
    })
    .join("");

  refs.catalogDropdown.innerHTML = `
    <div class="catalog-rail" role="none">${railHtml}</div>
    <div class="catalog-flyout" role="group" aria-label="${escapeHtml(activeCategory?.name ?? "")}">${flyoutHtml}</div>
  `;

  if (!state.catalogOpen) {
    refs.catalogDropdown.classList.add("hidden");
  } else {
    refs.catalogDropdown.classList.remove("hidden");
  }

  refs.catalogDropdown.querySelectorAll(".catalog-rail [data-hover-category]").forEach((item) => {
    const categoryId = (item as HTMLElement).dataset.hoverCategory;
    item.addEventListener("mouseenter", () => {
      if (!state.expandedCategories.has(categoryId!)) {
        state.expandedCategories.clear();
        state.expandedCategories.add(categoryId!);
        renderCatalog();
      }
    });
  });
}

/* ── Products count pill ── */

export function pluralizeProducts(n: number) {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs >= 11 && abs <= 19) return n + " товаров";
  if (last === 1) return n + " товар";
  if (last >= 2 && last <= 4) return n + " товара";
  return n + " товаров";
}

export function renderProductsCount() {
  const el = refs.productsCount;
  if (!el) return;

  // Страница товара — скрываем pill
  if (state.productPage) {
    el.classList.add("hidden");
    return;
  }

  const count = state.pagination?.total ?? 0;

  // Скрываем pill только до загрузки каталога; после — показываем всегда (даже «0 товаров»)
  if (state.catalogData.categories.length === 0) {
    el.classList.add("hidden");
    return;
  }

  el.textContent = pluralizeProducts(count);
  el.classList.remove("hidden");

  // Анимация только при реальном изменении числа (пропускаем первый рендер)
  if (_prevFilteredCount !== null && _prevFilteredCount !== count) {
    el.classList.remove("is-count-changed");
    void el.offsetWidth; // reflow — перезапуск анимации
    el.classList.add("is-count-changed");
  }

  _prevFilteredCount = count;
}

export function renderBreadcrumb() {
  if (!refs.breadcrumb) return;
  const title = document.getElementById("controls-title");

  // Страница товара — хлебные крошки «Каталог > Название»
  if (state.productPage) {
    if (title) title.classList.add("hidden");
    refs.breadcrumb.classList.remove("hidden");
    refs.breadcrumb.innerHTML = `
      <ol>
        <li><a href="${BASE_PATH}" data-action="reset-catalog">${CATALOG_ROOT_LABEL}</a><span class="breadcrumb-sep">&gt;</span></li>
        <li><span aria-current="page">${escapeHtml(state.productPage.name)}</span></li>
      </ol>
    `;
    return;
  }

  if (!state.breadcrumbPath) {
    refs.breadcrumb.classList.add("hidden");
    refs.breadcrumb.innerHTML = "";
    if (title) {
      // DES-S-03.3: корень — title hidden (см. аналог в render-b.js)
      title.classList.add("hidden");
      title.textContent = CATALOG_ROOT_LABEL;
    }
    return;
  }

  // Скрываем заголовок — его заменяют хлебные крошки
  if (title) title.classList.add("hidden");

  refs.breadcrumb.classList.remove("hidden");

  if (state.breadcrumbPath.subcategoryName) {
    refs.breadcrumb.innerHTML = `
      <ol>
        <li><a href="#" data-action="reset-catalog">${CATALOG_ROOT_LABEL}</a><span class="breadcrumb-sep">&gt;</span></li>
        <li><a href="#" data-action="select-category" data-category-id="${escapeHtml(state.selectedCategory ?? '')}">${escapeHtml(state.breadcrumbPath.categoryName ?? '')}</a><span class="breadcrumb-sep">&gt;</span></li>
        <li><a href="#" data-action="select-subcategory" data-category-id="${escapeHtml(state.selectedCategory ?? '')}" data-subcategory-id="${escapeHtml(state.selectedSubcategory ?? '')}" aria-current="page">${escapeHtml(state.breadcrumbPath.subcategoryName ?? '')}</a></li>
      </ol>
    `;
  } else {
    refs.breadcrumb.innerHTML = `
      <ol>
        <li><a href="#" data-action="reset-catalog">${CATALOG_ROOT_LABEL}</a><span class="breadcrumb-sep">&gt;</span></li>
        <li><a href="#" data-action="select-category" data-category-id="${escapeHtml(state.selectedCategory ?? '')}" aria-current="page">${escapeHtml(state.breadcrumbPath.categoryName ?? '')}</a></li>
      </ol>
    `;
  }
}

function renderSortSelect() {
  return `
    <label>
      Сортировка:
      <select data-action="set-sort">
        <option value=""${state.sortBy === "" ? " selected" : ""}>По умолчанию</option>
        <option value="price_asc"${state.sortBy === "price_asc" ? " selected" : ""}>Дешевле</option>
        <option value="price_desc"${state.sortBy === "price_desc" ? " selected" : ""}>Дороже</option>
      </select>
    </label>
  `;
}

/**
 * HTML глобальных фильтров: Цена от/до + Сортировка + В наличии.
 * Отображается всегда (на главной, в категории, в подкатегории), прижаты вправо.
 */
function renderGlobalFiltersHtml() {
  return `
    <label>
      Цена:
      <input type="number" data-action="set-price-min"
             placeholder="от" min="0" step="any"
             value="${escapeHtml(String(state.priceMin))}" />
      <span class="price-sep">&mdash;</span>
      <input type="number" data-action="set-price-max"
             placeholder="до" min="0" step="any"
             value="${escapeHtml(String(state.priceMax))}" />
    </label>
    ${renderSortSelect()}
    <label class="stock-label">
      <input type="checkbox" data-action="toggle-stock"
             ${state.stockOnly ? "checked" : ""} />
      В наличии
    </label>
    <label class="stock-label">
      <input type="checkbox" data-action="toggle-favorites-only"
             ${state.favoritesOnly ? "checked" : ""} />
      Избранное
    </label>
  `;
}

export function renderFilters() {
  if (!refs.filtersBar) return;

  // Страница товара — скрываем фильтры
  if (state.productPage) {
    refs.filtersBar.classList.add("hidden");
    refs.filtersBar.innerHTML = "";
    return;
  }

  // ── Случай 1: Категория выбрана, но НЕ подкатегория — кнопки подкатегорий + глобальные
  if (state.selectedCategory && !state.selectedSubcategory && state.breadcrumbPath) {
    const category = state.catalogData.categories.find(
      (c) => c.id === state.selectedCategory,
    );
    if (category && category.subcategories.length > 0) {
      refs.filtersBar.classList.remove("hidden");

      refs.filtersBar.innerHTML = `
        <div class="subcategory-buttons">
          ${category.subcategories
            .map(
              (sub) => `
              <a href="#" class="btn btn-outline btn-sm text-red"
                 data-action="select-subcategory"
                 data-category-id="${escapeHtml(category.id ?? '')}"
                 data-subcategory-id="${escapeHtml(sub.id ?? '')}">${escapeHtml(sub.name)}</a>
            `,
            )
            .join("")}
        </div>
        <div class="filters-row filters-global">
          ${renderGlobalFiltersHtml()}
        </div>
      `;
      return;
    }
  }

  // ── Случай 2: Подкатегория с характеристиками — фильтры характеристик + глобальные
  if (state.breadcrumbPath && state.currentFilters.length > 0) {
    refs.filtersBar.classList.remove("hidden");

    const selectHtml = state.currentFilters
      .map((filterDef) => {
        const code = typeof filterDef === "object" ? (filterDef as any).code : filterDef;
        const name = typeof filterDef === "object" ? (filterDef as any).name : filterDef;

        // Приоритет 1: серверные distinct-значения (полный список из всей выборки)
        // Приоритет 2: предопределённые значения из БД (znach)
        // Приоритет 3: значения из товаров текущей страницы (fallback)
        const serverVals = state.filterValues?.[code];
        const hasServerVals = Array.isArray(serverVals) && serverVals.length > 0;
        const hasZnach = typeof filterDef === "object" && Array.isArray((filterDef as any).values) && (filterDef as any).values.length > 0;
        let values;

        if (hasServerVals) {
          // Сервер отдал реальные уникальные значения для всей выборки — используем их.
          // Если есть znach — сортируем по порядку из админки.
          if (hasZnach) {
            const order = (filterDef as any).values;
            values = serverVals.slice().sort(function(a, b) {
              let ia = order.indexOf(a), ib = order.indexOf(b);
              if (ia === -1) ia = Infinity;
              if (ib === -1) ib = Infinity;
              return ia - ib;
            });
          } else {
            values = serverVals;
          }
        } else if (hasZnach) {
          values = (filterDef as any).values;
        } else {
          // Fallback: из текущей страницы товаров
          values = Array.from(new Set(
            state.products.map(function(p) { return p.filters?.[code]; }).filter(Boolean)
          ));
          values.sort();
        }

        if (values.length === 0) return "";

        return `
          <label>
            ${escapeHtml(name)}:
            <select data-action="set-filter" data-filter-name="${escapeHtml(code)}">
              <option value="">Все</option>
              ${values.map((value: string) => {
                const selected = state.activeFilters[code] === value;
                return `<option value="${escapeHtml(value)}" ${selected ? "selected" : ""}>${escapeHtml(value)}</option>`;
              }).join("")}
            </select>
          </label>
        `;
      })
      .join("");

    refs.filtersBar.innerHTML = `
      <div class="filters-row">
        ${selectHtml}
      </div>
      <div class="filters-row filters-global">
        ${renderGlobalFiltersHtml()}
      </div>
    `;
    return;
  }

  // ── Случай 3: Главная / категория без фильтров — только глобальные
  refs.filtersBar.classList.remove("hidden");
  refs.filtersBar.innerHTML = `
    <div class="filters-row filters-global">
      ${renderGlobalFiltersHtml()}
    </div>
  `;
}

/* ══════════════════════════════════════════════════════════════
   Переиспользуемые фрагменты карточки товара (для renderProducts + updateProductCard)
   ══════════════════════════════════════════════════════════════ */

/** HTML зоны действий grid-карточки: qty-controls / кнопка «В корзину» + card-total-line */
function renderGridCardActions(article: string, qty: number, price: number, stock: number, atLimit: boolean, outOfStock: boolean) {
  const escapedArticle = escapeHtml(article);
  const itemTotal = price * qty;

  if (qty > 0) {
    return `
      <div class="qty-controls">
        <button class="btn btn-outline icon-btn" data-action="change-qty" data-article="${escapedArticle}" data-delta="-1" aria-label="Уменьшить количество">${ICONS.minus}</button>
        <span class="qty-value">${qty}</span>
        <button class="btn btn-outline icon-btn" data-action="change-qty" data-article="${escapedArticle}" data-delta="1"${atLimit ? ' disabled title="Достигнут лимит остатка"' : ""} aria-label="Увеличить количество">${ICONS.plus}</button>
      </div>
      ${atLimit ? '<div class="stock-limit-hint">Доступно ' + stock + ' шт.</div>' : ""}
      <div class="card-total-line">
        <span class="card-total-label">Сумма:</span>
        <span class="card-total-amount">${formatPrice(itemTotal)}</span>${SF_CURRENCY_HTML}
      </div>
    `;
  }

  if (outOfStock) {
    return `<button class="btn btn-outline" data-action="stock-alert" data-article="${escapedArticle}">Сообщить</button>`;
  }

  return `<button class="btn btn-accent" data-action="add-to-cart" data-article="${escapedArticle}">${ICONS.plus} В корзину</button>`;
}

/** HTML содержимого .list-col-action для list-строки */
function renderListRowActions(article: string, qty: number, stock: number, atLimit: boolean, outOfStock: boolean) {
  const escapedArticle = escapeHtml(article);

  if (qty > 0) {
    return `
      <div class="stock-limit-slot stock-limit-slot-top is-hidden">&nbsp;</div>
      <div class="qty-controls">
        <button class="btn btn-outline icon-btn rounded-full" data-action="change-qty" data-article="${escapedArticle}" data-delta="-1" aria-label="Уменьшить количество">${ICONS.minusSm}</button>
        <span class="qty-value">${qty}</span>
        <button class="btn btn-outline icon-btn rounded-full" data-action="change-qty" data-article="${escapedArticle}" data-delta="1"${atLimit ? ' disabled title="Достигнут лимит остатка"' : ""} aria-label="Увеличить количество">${ICONS.plusSm}</button>
      </div>
      <div class="stock-limit-hint stock-limit-slot${atLimit ? "" : " is-hidden"}">Доступно ${stock} шт.</div>
    `;
  }

  if (outOfStock) {
    return `<button class="btn btn-outline btn-sm" data-action="stock-alert" data-article="${escapedArticle}">Сообщить</button>`;
  }

  return `<button class="btn btn-accent btn-sm" data-action="add-to-cart" data-article="${escapedArticle}">${ICONS.plusSm} В корзину</button>`;
}

/** HTML содержимого .list-col-total для list-строки */
function renderListRowTotal(itemTotal: number, qty: number) {
  if (qty <= 0) {
    return `
    <span class="list-total-label">Сумма:</span>
    <span class="list-total">—</span>
  `;
  }
  return `
    <span class="list-total-label">Сумма:</span>
    <span class="list-total">${formatPrice(itemTotal)}</span>${SF_CURRENCY_HTML}
  `;
}

/* ══════════════════════════════════════════════════════════════ */

export function renderProductsGrid(products: Product[]) {
  return `
    <div class="products-grid">
      ${products
        .map((product: Product) => {
          const qty = getCartQuantity(product.article);
          const firstImage = product.images?.[0] ?? "";
          const stock = product.stock ?? 0;
          const atLimit = stock > 0 && qty >= stock;
          const outOfStock = stock <= 0;
          return `
            <article class="product-card" data-article="${escapeHtml(product.article)}">
              <div class="product-image" data-action="open-product" data-article="${escapeHtml(product.article)}" tabindex="0" role="button">
                ${
                  firstImage
                    ? pictureTag(firstImage, product.name)
                    : `<div class="placeholder">${ICONS.packageLg}</div>`
                }
                ${renderGridBadgeStack(product.badges ?? [], product.oldPrice, product.price, stock)}
                ${renderFavBtn(product.article, 'fav-btn-card')}
              </div>
              <div class="product-content">
                <h3 class="product-title">
                  <span class="product-title-text" data-action="open-product" data-article="${escapeHtml(product.article)}" tabindex="0" role="button">${escapeHtml(product.name)}</span>
                  <a href="${BASE_PATH}/product/${encodeURIComponent(product.article)}"
                     class="product-page-link"
                     aria-label="Страница товара — ${escapeHtml(product.name)}"
                     title="Страница товара">${ICONS.moreVertical}</a>
                </h3>
                <div class="price-line"><span class="price-value">${formatPrice(product.price)}</span><span class="price-unit">\u00A0₽</span>${product.oldPrice && product.oldPrice > product.price ? `<span class="old-price">${formatMoney(product.oldPrice)}</span>` : ''}</div>
                ${renderGridCardActions(product.article, qty, product.price, stock, atLimit, outOfStock)}
              </div>
            </article>
          `;
        })
        .join("")}
    </div>
  `;
}

export function renderProductsList(products: Product[]) {
  return `
    <section class="products-list card">
      <div class="list-header">
        <div class="list-header-product">Товар</div>
        <div class="center">Цена</div>
        <div class="center">Шт.</div>
        <div class="center">Кол-во</div>
        <div class="center">Сумма</div>
      </div>
      <div class="list-body">
        ${products
          .map((product: Product) => {
            const qty = getCartQuantity(product.article);
            const firstImage = product.images?.[0] ?? "";
            const itemTotal = qty > 0 ? product.price * qty : 0;
            const stock = product.stock ?? 0;
            const atLimit = stock > 0 && qty >= stock;
            const outOfStock = stock <= 0;

            return `
              <div class="list-row" data-article="${escapeHtml(product.article)}">
                <div class="thumb-48 list-col-thumb" data-action="open-product" data-article="${escapeHtml(product.article)}" tabindex="0" role="button">
                  ${
                    firstImage
                      ? pictureTag(firstImage, product.name)
                      : `<div class="placeholder">${ICONS.packageSm}</div>`
                  }
                  ${renderFavBtn(product.article, 'fav-btn-list')}
                </div>

                <div class="list-col-name">
                  <h3 class="list-product-name">
                    <span class="product-title-text" data-action="open-product" data-article="${escapeHtml(product.article)}" tabindex="0" role="button">${escapeHtml(product.name)}</span>
                    <a href="${BASE_PATH}/product/${encodeURIComponent(product.article)}"
                       class="product-page-link"
                       aria-label="Страница товара — ${escapeHtml(product.name)}"
                       title="Страница товара">${ICONS.moreVertical}</a>
                  </h3>
                  ${renderCatalogListBadges(product.badges, product.oldPrice, product.price, stock)}
                </div>

                <div class="center list-col-price">
                  <div class="list-price-stack">
                    <div class="list-price-main">
                      <span class="list-price-amount">${formatPrice(product.price)}</span><span class="list-price-currency">\u00A0₽</span>
                    </div>
                    ${product.oldPrice && product.oldPrice > product.price ? `<span class="old-price">${formatMoney(product.oldPrice)}</span>` : ''}
                  </div>
                </div>

                <div class="center list-col-stock">
                  <span class="list-unit-badge">${stock}</span>
                </div>

                <div class="center list-col-action">
                  ${renderListRowActions(product.article, qty, stock, atLimit, outOfStock)}
                </div>

                <div class="center list-col-total${qty > 0 ? "" : " is-empty"}">
                  ${renderListRowTotal(itemTotal, qty)}
                </div>
              </div>
            `;
          })
          .join("")}
      </div>
    </section>
  `;
}

export function renderEmptyState() {
  if (state.loadError) {
    return `
      <div class="empty-state card">
        <div class="empty-state-icon">${ICONS.alertTriangle}</div>
        <p class="empty-state-message">Не удалось загрузить товары</p>
        <p class="block-lead">Проверьте соединение и повторите.</p>
        <div class="empty-state-actions">
          <button class="btn btn-accent btn-sm" data-action="retry-fetch">Повторить загрузку</button>
        </div>
      </div>
    `;
  }

  const hasSearch = state.searchQuery.trim().length > 0;
  const hasFilters = Object.keys(state.activeFilters).length > 0;
  const hasSort = state.sortBy !== "";
  const inCategory = !!state.selectedCategory;
  const categoryName = state.breadcrumbPath ? state.breadcrumbPath.categoryName ?? '' : "";
  const q = escapeHtml(state.searchQuery.trim());

  let title = "Ничего не найдено";
  let lead = "Попробуйте изменить условия или сбросить фильтры.";
  if (hasSearch && inCategory) {
    lead = `В категории «${escapeHtml(categoryName)}» по этому запросу пусто.`;
  } else if (hasSearch) {
    lead = `По запросу «${q}» ничего нет.`;
  } else if (hasFilters) {
    lead = "По выбранным фильтрам пусто.";
  } else if (inCategory) {
    title = "Здесь пока пусто";
    lead = "В этой категории нет товаров.";
  }

  const actions = [];

  // Умные кнопки-подсказки при поиске без результатов
  if (hasSearch) {
    const fallback = countFallbackResults(state.searchQuery);

    // Кнопка «Искать во всём каталоге» — если внутри категории и есть результаты глобально
    if (inCategory && fallback.globalCount > 0) {
      actions.push(
        `<button class="btn btn-accent btn-sm" data-action="search-global">` +
        `Искать «${escapeHtml(state.searchQuery.trim())}» во всём каталоге (${fallback.globalCount})` +
        `</button>`
      );
    }

    // Кнопка «Возможно, вы имели в виду...» — исправление опечаток (приоритетная подсказка)
    if (fallback.suggestion) {
      const suggest = fallback.suggestion as any;
      actions.push(
        `<button class="btn btn-accent btn-sm" data-action="search-suggest" data-suggest="${escapeHtml(suggest.text)}">` +
        `Возможно, вы имели в виду: «${escapeHtml(suggest.text)}» (${suggest.count})` +
        `</button>`
      );
    }

    // Кнопка «Показать частичные совпадения» — если есть OR-результаты
    if (fallback.partialCount > 0) {
      actions.push(
        `<button class="btn btn-outline btn-sm" data-action="search-partial">` +
        `Показать частичные совпадения (${fallback.partialCount})` +
        `</button>`
      );
    }

    actions.push(`<button class="btn btn-outline btn-sm" data-action="clear-search">Очистить поиск</button>`);
  }
  if (hasFilters) {
    actions.push(`<button class="btn btn-outline btn-sm" data-action="reset-filters">Сбросить фильтры</button>`);
  }
  if (hasSearch || hasFilters || hasSort) {
    actions.push(`<button class="btn btn-accent btn-sm" data-action="reset-catalog">Показать все товары</button>`);
  }

  return `
    <div class="empty-state card">
      <div class="empty-state-icon">${ICONS.packageLg}</div>
      <p class="empty-state-message">${title}</p>
      <p class="block-lead">${lead}</p>
      ${actions.length > 0 ? `<div class="empty-state-actions">${actions.join("")}</div>` : ""}
    </div>
  `;
}

/* ══════════════════════════════════════════════════════════════
   Страница товара — карточка вместо сетки
   ══════════════════════════════════════════════════════════════ */

export function renderProductPage() {
  if (!refs.products || !state.productPage) return;
  const p = state.productPage;
  const qty = getCartQuantity(p.article);
  const stock = p.stock ?? 0;
  const atLimit = stock > 0 && qty >= stock;
  const outOfStock = stock <= 0;
  const rawChars = p.characteristics || {};
  const chars = Object.keys(rawChars).length > 0
    ? rawChars
    : Object.fromEntries(
        Object.entries(p.filters || {}).map(([code, value]) => [filterCodeToName.get(code) || code, value]),
      );
  const charKeys = Object.keys(chars);

  const images = p.images || [];
  const imageIdx = images.length
    ? Math.min(Math.max(state.productPageImageIndex, 0), images.length - 1)
    : 0;
  state.productPageImageIndex = imageIdx;
  const mainImg = images[imageIdx] || "";
  const multiImages = images.length > 1;
  const galleryControlsHtml = multiImages
    ? `
      <button type="button" class="btn slider-btn slider-prev" data-action="product-page-prev-image">${ICONS.chevronLeft}</button>
      <button type="button" class="btn slider-btn slider-next" data-action="product-page-next-image">${ICONS.chevronRight}</button>
      <div class="product-page-image-counter image-counter">${imageIdx + 1} / ${images.length}</div>
    `
    : "";

  const thumbsHtml = multiImages
    ? `<div class="product-page-thumbs">
        ${images.map((img, i) => `
          <button type="button" class="product-page-thumb${i === imageIdx ? ' is-active' : ''}"
                  data-action="product-page-set-thumb" data-image-src="${escapeHtml(img)}" data-index="${i}"
                  aria-label="Фото ${i + 1}">
            ${pictureTag(img, '', 'lazy')}
          </button>
        `).join("")}
       </div>`
    : "";

  const actionsHtml = renderGridCardActions(p.article, qty, p.price, stock, atLimit, outOfStock);
  const badgesHtml = renderGridBadgeStack(p.badges ?? [], p.oldPrice ?? null, p.price, stock);

  const charsHtml = charKeys.length > 0
    ? `<div class="product-page-chars">
        <h3>Характеристики</h3>
        <table>
          ${charKeys.map(k => `
            <tr>
              <td class="char-name">${escapeHtml(k)}</td>
              <td class="char-value">${escapeHtml(chars[k])}</td>
            </tr>
          `).join("")}
        </table>
       </div>`
    : "";

  refs.products.innerHTML = `
    <div class="product-page-card">
      <div class="product-page-gallery">
        ${mainImg
          ? `<div class="product-page-main-image">
              ${pictureTag(mainImg, p.name, 'eager', {
                id: 'product-main-image',
                'data-action': 'open-product-page-viewer',
                'data-article': p.article,
              })}
              ${galleryControlsHtml}
              ${renderFavBtn(p.article, 'fav-btn-page')}
             </div>`
          : `<div class="product-page-main-image product-page-no-image">
              <div class="placeholder">${ICONS.packageXl}</div>
              ${renderFavBtn(p.article, 'fav-btn-page')}
             </div>`
        }
        ${thumbsHtml}
      </div>
      <div class="product-page-info">
        <div class="product-identity">
          <h1 class="product-page-name">${escapeHtml(p.name)}</h1>
          <p class="product-page-article">Артикул: ${escapeHtml(p.article)}</p>
        </div>
        <div class="product-commerce">
          <div class="product-page-price-row price-line">
            <span class="product-page-price price-value">${formatPrice(p.price)}</span><span class="price-unit">\u00A0₽</span>
            ${p.oldPrice && p.oldPrice > p.price ? `<span class="old-price">${formatMoney(p.oldPrice)}</span>` : ''}
            <span class="product-page-unit">/ ${escapeHtml(p.unit || 'шт.')}</span>
          </div>
          <div class="commerce-badges">${badgesHtml}</div>
          <div class="product-page-actions" data-article="${escapeHtml(p.article)}">
            ${actionsHtml}
          </div>
        </div>
        ${charKeys.length > 0 ? `<div class="product-depth">${charsHtml}</div>` : ""}
      </div>
    </div>
  `;
}

export function renderProducts() {
  if (!refs.products) return;

  if (!state.productPage) {
    removePdpCrossSell();
    document.getElementById('reviews-block')?.remove();
  }

  // SSR hydration: сервер уже отрендерил карточки — пропускаем полную перерисовку.
  // Снимаем флаг и data-ssr, чтобы следующий вызов (навигация, фильтры) работал обычно.
  // Но точечно обновляем карточки товаров из корзины (SSR не знает localStorage).
  if (state.ssrHydrated) {
    state.ssrHydrated = false;
    const ssrSection = document.querySelector('#products[data-ssr]');
    if (ssrSection) ssrSection.removeAttribute('data-ssr');
    // SSR не знает о localStorage-корзине — точечно обновляем карточки товаров в корзине
    if (state.cart.length > 0) {
      for (const item of state.cart) {
        updateProductCard(item.article);
      }
    }
    return;
  }

  // Страница товара — рендерим карточку вместо сетки
  if (state.productPage) {
    renderProductPage();
    void mountPdpCrossSell();
    return;
  }

  // state.products уже содержит ровно одну страницу товаров с сервера
  const products = state.products;

  if (products.length === 0) {
    refs.products.innerHTML = renderEmptyState();
    return;
  }

  refs.products.innerHTML =
    state.viewMode === "grid"
      ? renderProductsGrid(products)
      : renderProductsList(products);
}

/**
 * Точечное обновление одной карточки товара при изменении корзины (Фаза 3).
 * Вместо пересоздания всех 12 карточек через innerHTML — патчим только изменившуюся.
 *
 * Два режима:
 *  - Структурный (0↔>0): заменяем innerHTML зоны действий одной карточки
 *  - Точечный (N→N±1, N>0): обновляем textContent / disabled на 3–6 элементах
 *
 * Если карточка не найдена в DOM (товар на другой странице) — тихий выход.
 */
export function updateProductCard(article: string) {
  if (!refs.products) return;

  // Страница товара — обновляем зону .product-page-actions
  if (state.productPage && state.productPage.article === article) {
    const actionsWrap = refs.products.querySelector(".product-page-actions");
    if (actionsWrap) {
      const p = state.productPage;
      const qty = getCartQuantity(article);
      const stock = p.stock ?? 0;
      const atLimit = stock > 0 && qty >= stock;
      const outOfStock = stock <= 0;
      actionsWrap.innerHTML = renderGridCardActions(article, qty, p.price, stock, atLimit, outOfStock);
    }
    return;
  }

  const card = refs.products.querySelector(
    '[data-article="' + CSS.escape(article) + '"]'
  );
  if (!card) return; // товар не на текущей странице

  const product = state.products.find(function (p) { return p.article === article; });
  if (!product) return;

  const qty = getCartQuantity(article);
  const stock = product.stock ?? 0;
  const atLimit = stock > 0 && qty >= stock;
  const outOfStock = stock <= 0;

  // Определяем предыдущее количество из DOM
  const qtyEl = card.querySelector(".qty-value");
  const prevQty = qtyEl ? parseInt(qtyEl.textContent, 10) : 0;

  // ── Структурный переход: 0↔>0 — HTML зоны действий полностью меняется ──
  if ((prevQty === 0 && qty > 0) || (prevQty > 0 && qty === 0)) {
    if (state.viewMode === "grid") {
      // Заменяем содержимое .product-content после price-line
      const content = card.querySelector(".product-content");
      if (content) {
        const priceLine = content.querySelector(".price-line");
        // Удаляем всё после price-line
        while (priceLine && priceLine.nextSibling) {
          priceLine.nextSibling.remove();
        }
        // Вставляем новый HTML
        priceLine!.insertAdjacentHTML("afterend", renderGridCardActions(article, qty, product.price, stock, atLimit, outOfStock));
      }
    } else {
      // List view: заменяем содержимое .list-col-action
      const actionCol = card.querySelector(".list-col-action");
      if (actionCol) {
        actionCol.innerHTML = renderListRowActions(article, qty, stock, atLimit, outOfStock);
      }
      // Обновляем .list-col-total (класс is-empty + содержимое)
      const totalCol = card.querySelector(".list-col-total");
      if (totalCol) {
        const itemTotal = qty > 0 ? product.price * qty : 0;
        totalCol.classList.toggle("is-empty", qty === 0);
        totalCol.innerHTML = renderListRowTotal(itemTotal, qty);
      }
    }
    return;
  }

  // ── Точечные правки: N → N±1 (N > 0) ──
  if (qtyEl) qtyEl.textContent = String(qty);

  // Сумма
  const itemTotal = product.price * qty;
  if (state.viewMode === "grid") {
    const totalEl = card.querySelector(".card-total-amount");
    if (totalEl) totalEl.textContent = formatPrice(itemTotal);
  } else {
    const totalEl = card.querySelector(".list-total");
    if (totalEl) totalEl.textContent = formatPrice(itemTotal);
  }

  // Кнопка «+» — disabled при лимите
  const plusBtn = card.querySelector('[data-delta="1"]') as HTMLButtonElement | null;
  if (plusBtn) {
    plusBtn.disabled = atLimit;
    plusBtn.title = atLimit ? "Достигнут лимит остатка" : "";
  }

  // Подсказка лимита
  if (state.viewMode === "grid") {
    // Grid: hint добавляется/удаляется из DOM (нет is-hidden)
    const hint = card.querySelector(".stock-limit-hint");
    if (atLimit && !hint) {
      const controls = card.querySelector(".qty-controls");
      if (controls) {
        controls.insertAdjacentHTML("afterend", '<div class="stock-limit-hint">Доступно ' + stock + ' шт.</div>');
      }
    } else if (!atLimit && hint) {
      hint.remove();
    }
  } else {
    // List: hint скрывается через is-hidden
    const hint = card.querySelector(".stock-limit-hint");
    if (hint) hint.classList.toggle("is-hidden", !atLimit);
  }
}

/**
 * Генерирует массив элементов пагинации: номера страниц и «…» (null).
 * При totalPages <= 7 — все страницы. Иначе — окно: 1 … cur-1 cur cur+1 … last.
 */
function paginationWindow(current: number, total: number) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = new Set([1, total]);
  for (let i = current - 1; i <= current + 1; i++) {
    if (i >= 1 && i <= total) pages.add(i);
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null); // «…»
    result.push(sorted[i]);
  }
  return result;
}

export function renderPagination() {
  if (!refs.pagination) return;

  // Страница товара — скрываем пагинацию
  if (state.productPage) {
    refs.pagination.innerHTML = "";
    return;
  }

  const totalPages = state.pagination?.totalPages ?? 1;
  if (totalPages <= 1) {
    refs.pagination.innerHTML = "";
    return;
  }

  const cur = state.currentPage;
  const pages = paginationWindow(cur, totalPages);

  const prevDisabled = cur <= 1 ? " disabled" : "";
  const nextDisabled = cur >= totalPages ? " disabled" : "";

  refs.pagination.innerHTML = `
    <nav aria-label="Пагинация">
      <span>Страница:</span>
      <button
        class="btn btn-outline btn-sm page-btn"
        data-action="set-page"
        data-page="${cur - 1}"
        aria-label="Предыдущая страница"${prevDisabled}
      >&lsaquo;</button>
      ${pages
        .map((p) => {
          if (p === null) return '<span class="pagination-ellipsis">&hellip;</span>';
          const activeClass = cur === p ? "active" : "";
          const ariaCurrent = cur === p ? ' aria-current="page"' : "";
          return `
            <button
              class="btn btn-outline btn-sm page-btn ${activeClass}"
              data-action="set-page"
              data-page="${p}"
              aria-label="Страница ${p}"${ariaCurrent}
            >${p}</button>`;
        })
        .join("")}
      <button
        class="btn btn-outline btn-sm page-btn"
        data-action="set-page"
        data-page="${cur + 1}"
        aria-label="Следующая страница"${nextDisabled}
      >&rsaquo;</button>
    </nav>
  `;
}

export function renderModal() {
  if (!refs.modal) return;
  if (!state.selectedProduct) {
    refs.modal.classList.remove("is-open");
    const dur = parseFloat(getComputedStyle(refs.modal).transitionDuration) * 1000 || 260;
    setTimeout(() => {
      refs.modal!.innerHTML = "";
    }, dur);
    unlockBodyScroll();
    return;
  }

  lockBodyScroll();

  const product = state.selectedProduct;
  const images = product.images ?? [];
  const hasImages = images.length > 0;
  const imageIndex = Math.min(
    Math.max(state.currentImageIndex, 0),
    Math.max(images.length - 1, 0),
  );
  state.currentImageIndex = imageIndex;

  const stock = product.stock ?? 0;
  const multiImages = images.length > 1;
  const modalBadgesHtml = renderGridBadgeStack(
    product.badges ?? [],
    product.oldPrice ?? null,
    product.price,
    stock,
  );
  const modalPropsHtml =
    Object.keys(product.filters ?? {}).length > 0
      ? `
            <div>
              <h3 class="props-heading">Характеристики</h3>
              <div class="props">
                ${Object.entries(product.filters ?? {})
                  .map(([filterCode, filterValue]) => {
                    const displayName = filterCodeToName.get(filterCode) || filterCode;
                    return `
                      <div class="prop-row">
                        <span>${escapeHtml(displayName)}</span>
                        <span>${escapeHtml(filterValue)}</span>
                      </div>
                    `;
                  })
                  .join("")}
              </div>
            </div>
          `
      : "";

  refs.modal.innerHTML = `
    <div class="modal-backdrop" data-action="close-modal"></div>
    <div class="modal-dialog">
      <button class="btn modal-close" data-action="close-modal" aria-label="Закрыть">${ICONS.x}</button>
      <div class="modal-grid">
        <div>
          <div class="modal-main-image">
            ${
              hasImages
                ? pictureTag(images[imageIndex], product.name, 'eager', {
                    'data-action': 'open-image-viewer',
                  })
                : `<div class="placeholder">${ICONS.packageXl}</div>`
            }
            ${
              multiImages
                ? `
                  <button type="button" class="btn slider-btn slider-prev" data-action="modal-prev-image">${ICONS.chevronLeft}</button>
                  <button type="button" class="btn slider-btn slider-next" data-action="modal-next-image">${ICONS.chevronRight}</button>
                  <div class="image-counter">${imageIndex + 1} / ${images.length}</div>
                `
                : ""
            }
            ${renderFavBtn(product.article, 'fav-btn-modal')}
          </div>
          ${
            hasImages
              ? `
                <div class="thumbs">
                  ${images
                    .map((image, thumbIndex) => {
                      const active = thumbIndex === imageIndex ? "active" : "";
                      return `
                        <button class="${active}" data-action="modal-set-image" data-image-index="${thumbIndex}">
                          ${pictureTag(image, `Миниатюра ${thumbIndex + 1}`, 'lazy')}
                        </button>
                      `;
                    })
                    .join("")}
                </div>
              `
              : ""
          }
        </div>
        <div class="modal-info">
          <div class="product-identity">
            <h2 class="modal-title" id="modal-product-title">${escapeHtml(product.name)}</h2>
            <p class="modal-meta">Артикул: ${escapeHtml(product.article)}</p>
          </div>
          <div class="product-commerce">
            <div class="price-box">
              <div class="price-row price-line">
                <span class="price-value">${formatPrice(product.price)}</span><span class="price-unit">\u00A0₽</span>
                ${product.oldPrice && product.oldPrice > product.price ? `<span class="old-price">${formatMoney(product.oldPrice)}</span>` : ''}
                <span class="product-page-unit">/ ${escapeHtml(product.unit || 'шт.')}</span>
              </div>
            </div>
            <div class="commerce-badges">${modalBadgesHtml}</div>
            <div class="modal-cart-actions" data-article="${escapeHtml(product.article)}">
              ${renderGridCardActions(product.article, getCartQuantity(product.article), product.price, product.stock ?? 0, (product.stock > 0 && getCartQuantity(product.article) >= (product.stock ?? 0)), (product.stock ?? 0) <= 0)}
            </div>
          </div>
          ${modalPropsHtml ? `<div class="product-depth">${modalPropsHtml}</div>` : ""}
        </div>
      </div>
      <a href="${BASE_PATH}/product/${encodeURIComponent(product.article)}"
         class="btn btn-outline modal-product-link">
         ${ICONS.moreVertical} Подробнее о товаре
      </a>
    </div>
    ${state.imageViewerOpen && hasImages ? `
      <div class="image-viewer">
        <div class="image-viewer-backdrop" data-action="close-image-viewer"></div>
        <div class="image-viewer-content">
          ${pictureTag(images[imageIndex], product.name, 'eager')}
          ${images.length > 1 ? `
            <button class="btn slider-btn slider-prev" data-action="viewer-prev-image">${ICONS.chevronLeft}</button>
            <button class="btn slider-btn slider-next" data-action="viewer-next-image">${ICONS.chevronRight}</button>
          ` : ""}
          <div class="image-counter">${imageIndex + 1} / ${images.length}</div>
        </div>
        <button class="btn modal-close" data-action="close-image-viewer">${ICONS.x}</button>
      </div>
    ` : ""}
  `;

  requestAnimationFrame(() => {
    refs.modal!.classList.add("is-open");
  });

  // Touch-свайп для изображений (модалка + полноэкранный просмотрщик)
  if (hasImages && images.length > 1) {
    const mainImageEl = refs.modal.querySelector(".modal-main-image");
    initSwipe(mainImageEl as HTMLElement | null, function () { shiftModalImage(1); updateModalImage(); }, function () { shiftModalImage(-1); updateModalImage(); });

    const viewerEl = refs.modal.querySelector(".image-viewer-content");
    initSwipe(viewerEl as HTMLElement | null, function () { shiftModalImage(1); updateModalImage(); }, function () { shiftModalImage(-1); updateModalImage(); });
  }
}

export function renderViewModeButtons() {
  const toggleWrap = document.querySelector(".view-toggle") as HTMLElement | null;
  if (toggleWrap) {
    toggleWrap.style.display = state.productPage ? "none" : "";
  }
  document.querySelectorAll('[data-action="set-view"]').forEach((button) => {
    const isActive = (button as HTMLElement).dataset.mode === state.viewMode;
    button.classList.toggle("active", isActive);
  });
}

/** Точечное обновление изображения в модалке (без пересоздания DOM) */
export function updateModalImage() {
  if (!refs.modal || !state.selectedProduct) return;
  const product = state.selectedProduct;
  const idx = state.currentImageIndex;
  const images = product.images;
  if (!images.length) return;

  // 1. Главное изображение модалки
  const mainImg = refs.modal.querySelector('.modal-main-image img') as HTMLImageElement | null;
  if (mainImg) {
    setPictureImageSrc(mainImg, images[idx], product.name);
  }

  // 2. Счётчики «2 / 5» (в модалке и в image-viewer)
  refs.modal.querySelectorAll('.image-counter').forEach(el => {
    el.textContent = (idx + 1) + ' / ' + images.length;
  });

  // 3. Активная миниатюра
  refs.modal.querySelectorAll('.thumbs button').forEach((btn, i) => {
    btn.classList.toggle('active', i === idx);
  });

  // 4. Image viewer (если открыт)
  if (state.imageViewerOpen) {
    const viewerImg = refs.modal.querySelector('.image-viewer-content img') as HTMLImageElement | null;
    if (viewerImg) {
      setPictureImageSrc(viewerImg, images[idx], product.name);
    }
  }
}

/** Точечное обновление галереи на PDP (без полного renderProductPage) */
export function updateProductPageGallery() {
  if (!refs.products || !state.productPage) return;
  const p = state.productPage;
  const images = p.images || [];
  if (!images.length) return;

  const idx = Math.min(Math.max(state.productPageImageIndex, 0), images.length - 1);
  state.productPageImageIndex = idx;

  const mainImg = document.getElementById('product-main-image') as HTMLImageElement | null;
  if (mainImg) {
    setPictureImageSrc(mainImg, images[idx], p.name);
  }

  refs.products.querySelectorAll('.product-page-image-counter').forEach(el => {
    el.textContent = `${idx + 1} / ${images.length}`;
  });

  const wrap = refs.products.querySelector('.product-page-thumbs');
  if (wrap) {
    wrap.querySelectorAll('.product-page-thumb').forEach((btn, i) => {
      btn.classList.toggle('is-active', i === idx);
    });
  }
}

/** Точечно обновляет зону кнопок корзины (.modal-cart-actions) внутри модалки */
export function updateModalActions(article: string) {
  if (!refs.modal || !state.selectedProduct) return;
  const wrap = refs.modal.querySelector('.modal-cart-actions');
  if (!wrap) return;
  const p = state.selectedProduct;
  const qty = getCartQuantity(article);
  const stock = p.stock ?? 0;
  const atLimit = stock > 0 && qty >= stock;
  const outOfStock = stock <= 0;
  wrap.innerHTML = renderGridCardActions(article, qty, p.price, stock, atLimit, outOfStock);
}

/** Точечное обновление кнопок избранного в DOM (без rerender) */
export function updateFavButton(article: string) {
  const active = isFavorite(article);
  const selector = '.fav-btn[data-article="' + CSS.escape(article) + '"]';
  document.querySelectorAll(selector).forEach(function (btn) {
    btn.classList.toggle('is-fav', active);
    btn.innerHTML = active ? ICONS.heartFilled : ICONS.heart;
    btn.setAttribute('aria-label', active ? 'Убрать из избранного' : 'Добавить в избранное');
    btn.setAttribute('aria-pressed', String(active));
    if (active) {
      btn.classList.remove('is-fav-pulse');
      void (btn as HTMLElement).offsetWidth; // reflow — перезапуск анимации
      btn.classList.add('is-fav-pulse');
    }
  });
  renderBottomNavFavBadge();
}

/** Помечает классом is-clamped заголовки, текст которых обрезан line-clamp */
export function markClampedTitles() {
  document.querySelectorAll('.product-title-text').forEach(el => {
    const parent = el.closest('.product-title, .list-product-name');
    if (!parent) return;
    parent.classList.toggle('is-clamped', el.scrollHeight > el.clientHeight + 1);
  });
}

export function render() {
  renderViewModeButtons();
  renderCartBadge();
  renderCartSummary();
  renderCartItems();
  renderCatalog();
  renderBreadcrumb();
  renderProductsCount();
  renderFilters();
  renderProducts();
  renderPagination();
  renderModal();
  updateFloatingCartText();
  updateFloatingCartVisibility();
  markClampedTitles();
  updateDocumentTitle();
  renderBottomNavFavBadge();
}

export { showToast } from './toast';

export function renderSyncIndicator() {
  const btn = document.querySelector('.cart-button');
  if (!btn) return;
  btn.classList.toggle('is-sync-failed', state.syncFailed);
}
